import React, { ReactNode } from 'react';
import cx from 'classnames';

import { Slider, SliderWithDiscreteSteps } from '@trqk/components';

import { futurePlanPadding } from '../../constants';
import { getPaymentNumberFormat } from '../../utils';

interface ChoosePlanSliderProps {
    children?: ReactNode;
    /**
     * State of the current value
     */
    currentValue: number;
    /**
     * The default value for the slider
     */
    defaultValue: number;
    /**
     * The minimum value allowed for a valid selection, based upon the user's current usage
     */
    minValueAllowed: number;
    /**
     * Method called with the next plan setting value when changed by the slider
     */
    onChange?: (value: number) => void;
    width: number;
    /**
     * Label for the payment setting
     */
    label: string;
    /**
     * Config for the underlying Slider component, if using programmatic step values determined by
     * `minValue`, `maxValue` and `step`.
     */
    sliderStepConfig?: SliderStepConfig;
    /**
     * Steps to be used in the underlying Slider component. This allows for an explicit list of step
     * values, rather than a programmatic one.
     */
    sliderSteps?: number[];
    /**
     * Type for the payment setting - see {@link PaymentSettingType}
     */
    paymentSettingType: PaymentSettingType;
}

const inputLabelClasses = 'text-sm text-tertiary-400 font-semibold';

/**
 * A plan setting that displays the amount available for a "future" plan, i.e. one the user can
 * choose to switch to. Includes a slider which can be used to change the amount available.
 * @param props - see {@link ChoosePlanSliderProps}
 */
export const ChoosePlanSlider = ({
    children,
    currentValue,
    defaultValue,
    minValueAllowed,
    onChange,
    width,
    label,
    sliderStepConfig,
    sliderSteps,
    paymentSettingType,
}: ChoosePlanSliderProps): JSX.Element => {
    const numberFormat = getPaymentNumberFormat(paymentSettingType);
    const sliderLabelWidth = width - futurePlanPadding * 2;
    const isValid = currentValue >= minValueAllowed;

    const inputClassConfig: InputClassConfig = {
        base: 'p-2 small-padding rounded border border-white',
        blur: cx({
            'border-tertiary-200': isValid,
            'border-quinary-500': !isValid,
        }),
        focus: 'border-secondary-500',
    };

    return (
        <div>
            <div className="space-y-4">
                {sliderStepConfig && (
                    <div className="flex justify-center">
                        <Slider
                            defaultValue={[defaultValue]}
                            label={label}
                            inputClassConfig={inputClassConfig}
                            inputLabelClasses={inputLabelClasses}
                            inputWidth={100}
                            onChange={(value: number[]): void => {
                                // The Slider onChange method expects a number array; we want the
                                // first number in the array
                                if (onChange) {
                                    onChange(value[0] ?? 0);
                                }
                            }}
                            numberFormat={numberFormat}
                            maxValue={sliderStepConfig.maxValue}
                            minValue={sliderStepConfig.minValue}
                            width={sliderLabelWidth}
                            step={sliderStepConfig.step}
                            roundType="up"
                        />
                    </div>
                )}
                {sliderSteps && (
                    <div className="flex justify-center">
                        <SliderWithDiscreteSteps
                            defaultStepIndex={
                                sliderSteps.indexOf(minValueAllowed) ?? 0
                            }
                            label={label}
                            inputClassConfig={inputClassConfig}
                            inputLabelClasses={inputLabelClasses}
                            numberFormat={numberFormat}
                            onChange={(value: number[]): void => {
                                // The SliderWithDiscreteSteps onChange method expects a number
                                // array; we want the first number in the array
                                if (onChange) {
                                    onChange(value[0] ?? 0);
                                }
                            }}
                            roundType="up"
                            width={sliderLabelWidth}
                            steps={sliderSteps}
                        />
                    </div>
                )}
            </div>
            {children}
        </div>
    );
};
